import {
  FixMeLater,
  ManufacturerOption,
  SaleAmount,
  SaleItem,
  SalePayment
} from 'src/types';
import { v4 as uuidv4 } from 'uuid';
import _ from 'lodash';

const formatStringCurrency = (value: any) => {
  if (value !== undefined && value !== null) {
    return `₱${parseFloat(value).toFixed(2)}`;
  }
  return '₱0.00';
};

export const parse = (data: FixMeLater) => {
  try {
    return JSON.parse(data);
  } catch (e) {
    console.log(`Oops! Some problems parsing this ${typeof data}.`);
  }

  return null;
};

export const cleanSN = (sn: string) => {
  return sn.trim().toUpperCase();
};

export const isValidCurrency = (input: any, allowZero: boolean = false) => {
  if (!isNaN(input)) {
    if (allowZero && +input === 0) {
      return true;
    }
    if (+input > 0) {
      return true;
    }
  }
  return false;
};

export const isEmptyOrSpaces = (str: string | undefined) => {
  return str === undefined || str === null || str?.match(/^ *$/) !== null;
};

export const snakeToNaturalCase = (str: string) => {
  return str.replace(/_/g, ' ');
};

export const snakeToTitleCase = (str: string) =>
  _.startCase(str.replace(/_/g, ' '));

export const naturalToSnakeCase = (str: string) => {
  return str.replace(/\s/g, '_');
};

export const manufacturerIDToName = (
  id: number | undefined,
  options: ManufacturerOption[] | undefined
) => {
  if (!id || !options) {
    return null;
  }
  return options.find((i) => i.id === id)?.label;
};

export const toTitleCase = (str: string) => {
  return str.replace(/\w\S*/g, function(txt) {
    return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
  });
};

export const isValidHttpUrl = (str: string) => {
  let url;
  try {
    url = new URL(str);
  } catch (_) {
    return false;
  }
  return url.protocol === 'http:' || url.protocol === 'https:';
};

export const initializedWordsOtherThanFirst = (str: string) => {
  let first = '';
  let initials = '';
  const words = str?.split(/\s/);

  if (words && words?.length > 0) {
    first = words[0];
    const initialsArr = words?.map((x) => x?.substring(0, 1)?.toUpperCase());
    initialsArr?.shift();
    initials = initialsArr?.join('');
  }

  return `${first} ${initials}`;
};

export const normalizeFieldText = (txt: string) => {
  if (typeof txt !== 'string') {
    return '';
  }

  return txt.replace(/\//g, '');
};

export const salesDataToString = (arr?: any[]) =>
  arr?.reduce((string, itemsObj) => {
    const itemDetails = Object.keys(itemsObj).map(
      (key) => `${key}: ${itemsObj[key]}`
    );
    return string.concat(`${itemDetails.join(', ')}\r\n`);
  }, '');

export const salesAmountsToString = (amount?: SaleAmount) => {
  // Create the formatted string
  const amountDetails = `
  Total Retail Price: ${formatStringCurrency(amount?.total_retail_price)}
  TDP: ${formatStringCurrency(amount?.total_dealer_price)}
  Amount Paid: ${formatStringCurrency(amount?.amount_paid)}
  Balance: ${formatStringCurrency(amount?.balance)}
  Gross: ${formatStringCurrency(amount?.gross)}
  `;

  return amountDetails;
};

export const salesPaymentToString = (arr?: SalePayment[]) =>
  arr?.reduce((string, itemsObj) => {
    // Extract necessary details
    const { amount, date_posted, payment_type } = itemsObj;

    // Create the formatted string
    const itemDetails = `${amount} (${payment_type})\n${date_posted}\n___________________________________`;

    return string.concat(`${itemDetails}\r\n`);
  }, '');

export const salesItemToString = (arr?: SaleItem[]) =>
  arr?.reduce((string, itemsObj) => {
    // Extract necessary details
    const {
      product_name,
      serial_no,
      quantity,
      dealers_price,
      retail_price
    } = itemsObj;

    // Create the formatted string
    const itemDetails = `${product_name}\n${serial_no}\nQTY: ${quantity} | DP: ${formatStringCurrency(
      dealers_price
    )} | RP: ${formatStringCurrency(
      retail_price
    )}\n___________________________________`;
    return string.concat(`${itemDetails}\r\n`);
  }, '');

//09 format
export const phPhoneNumberChecker = (phone?: string) => {
  if (phone) {
    if (phone.length === 11) {
      // Require "09" followed by 9 digits
      return /^09\d{9}$/.test(phone);
    } else if (phone.length === 8) {
      // Allow 8 digits for telephone
      return /^\d{8}$/.test(phone);
    }
  }
  return false;
};

export const containsAlphabetsWithSpacesAndDashes = (txt: string): boolean => {
  if (typeof txt !== 'string') {
    return false;
  }

  return /[A-Za-z]/.test(txt) && /^[A-Za-z0-9\s-]+$/.test(txt);
};

export const secureText = (input: string) => {
  return input
    .split(' ')
    .map((word) => {
      if (word.length <= 1) return word; // For single letter words, no transformation needed
      // For the last word in the name
      if (word === input.split(' ').slice(-1)[0]) {
        return `${word[0]}${'*'.repeat(word.length - 2)}${word.slice(-1)}`;
      }
      // For other words in the name
      return `${word[0]}${'*'.repeat(word.length - 1)}`;
    })
    .join(' ');
};

export const allowStringOnlyInput = (value: string): boolean => {
  // Regex: allows letters and spaces but no leading/trailing spaces
  const regex = /^[A-Za-z]+(\s[A-Za-z]*)*$/;
  return regex.test(value) || value === '';
};

export const getRandomUUIDv4Short = () => uuidv4()?.substring(0, 8);

export const reverseString = (str: string): string => {
  if (!str) {
    return '';
  }
  let newString = '';
  for (let i = str?.length - 1; i >= 0; i--) {
    newString += str[i];
  }
  return newString;
};

export const snakeCaseToTitleCase = (snakeStr?: string) => {
  return snakeStr
    ?.toLowerCase() // Convert the string to lowercase
    .split('_') // Split the string into parts by underscores
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1)) // Capitalize the first letter of each word
    .join(' '); // Join the parts with spaces
};

export const buildQueryString = (params: {
  [key: string]: string | number | string[] | number[];
}) => {
  const urlParams = Object.entries(params)
    .filter(
      // eslint-disable-next-line no-unused-vars
      ([_, value]) => value !== null && value !== undefined && value !== ''
    ) // Remove empty values
    .flatMap(
      ([key, value]) =>
        Array.isArray(value)
          ? value.map((v) => `${key}=${encodeURIComponent(v)}`) // Handle arrays
          : `${key}=${encodeURIComponent(value)}` // Handle single values
    )
    .join('&');

  return urlParams;
};

export const parseSearch = (
  search: string
): { [key: string]: string | string[] } => {
  if (!search.trim()) return {};

  return (
    search
      .substring(1)
      .split('&')
      // any type bandaid for ts error
      .reduce((acc: { [key: string]: any }, param) => {
        const [key, value] = param.split('=');
        const decodedValue = decodeURIComponent(value || '');

        // Handle duplicate keys eg: ?name=John&name=Jane
        // This will make exisiting prop name: john into name: [john, jane]
        if (acc[key]) {
          acc[key] = Array.isArray(acc[key])
            ? [...acc[key], decodedValue]
            : [acc[key], decodedValue];
        } else {
          acc[key] = decodedValue;
        }

        return acc;
      }, {})
  );
};

// manual parser for location search value because the built in parser "new URLSearchParams()" does not work
export const parseSearchV2 = (search: string): { [key: string]: string } => {
  if (!search) return {};

  // response of this function is intended to mimic new URLSearchParams()
  return search
    .substring(1)
    .split('&')
    .reduce((acc: { [key: string]: string }, param) => {
      const [key, value] = param.split('=');
      acc[key] = decodeURIComponent(value || ''); // Handle undefined cases
      return acc;
    }, {});
};

// response of this function is intended to mimic new url slugs
export const objectToUrlSlug = (payload: { [key: string]: any }): string => {
  if (!payload) return '';

  const entries = Object.entries(payload);

  return entries.map((i) => `${i[0]}=${i[1]}`).join('&');
};

export const stringToNumberArr = (val: string): number[] | null => {
  if (!val) {
    return null;
  }

  return val.split(',').map((i): number => +i) ?? null;
};

export const abbreviateBranchName = (branchName: string) => {
  const map: { [key: string]: string } = {
    'Earnshaw - Manila': 'MNL',
    'LPC - Alabang Zapote': 'LPC',
    'PC Worth Experience (QC) - Main': 'QC'
  };

  return map[branchName] ?? branchName;
};

export const branchName = (branchName: string) => {
  const map: { [key: string]: string } = {
    'Earnshaw - Manila': 'EARNSHAW',
    'LPC - Alabang Zapote': 'LPC',
    'PC Worth Experience (QC) - Main': 'EXPERIENCE'
  };

  return map[branchName] ?? branchName;
};

export const isNumberRegex = (value: string) => {
  return /^\d*$/.test(value);
};
